import { gql, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { UserContext, usePermissions } from '@providers/userContextProvider';
import { isNull } from 'lodash-es';
import type { ChangeEvent } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

export type AssetClassSelectItem = {
  id: string;
  key?: string;
  name?: string;
  translatedName?: TranslatedString;
};

export const FETCH_ASSET_CLASSES = gql`
  query fetchAssetClasses($input: FetchAssetClassesInput) {
    fetchAssetClasses(input: $input) {
      assetClasses {
        id
        key
        name
        translatedName { ${TRANSLATED_STRING_FIELDS} }
      }
    }
  }
`;

export const AssetClassSelect = ({
  setAssetClass,
  onSelectFull,
  value,
  label,
  size,
  disabledWith = null,
  filteredAllocations = [],
  omitAny = false,
  leadingIcon,
  required = false,
  testId = 'asset-class-select',
}: {
  setAssetClass: (event: ChangeEvent<HTMLInputElement>) => void;
  onSelectFull?: (item: AssetClassSelectItem) => void;
  value: string;
  label: string;
  size?: 'small' | 'medium';
  disabledWith?: string | null;
  filteredAllocations?: string[];
  omitAny?: boolean;
  leadingIcon?: 'dollar' | 'percent' | 'search';
  required?: boolean;
  testId?: string;
}) => {
  const { t } = useTranslation(['configureModels']);
  const { permissions } = usePermissions();
  const { activeOrganization } = useContext(UserContext);
  const { loading, data } = useQuery(FETCH_ASSET_CLASSES, {
    variables: {
      input: {
        filter: { organizationId: activeOrganization.id },
      },
    },
  });

  const list: AssetClassSelectItem[] =
    data?.fetchAssetClasses?.assetClasses || [];
  const filteredList =
    filteredAllocations.length === 0
      ? list
      : list.filter(
          (i) => i.id === value || !filteredAllocations.includes(i.id ?? ''),
        );

  const renderMenuItems = () => {
    if (loading) {
      return (
        <MenuItem data-testid={`${testId}-menu-item-loading`}>...</MenuItem>
      );
    }

    if (disabledWith) {
      return (
        <MenuItem
          key={value}
          value={value}
          data-testid={`${testId}-menu-item-disabled-${disabledWith}`}
        >
          <b>{disabledWith}</b>
        </MenuItem>
      );
    }

    return filteredList?.map((x) => (
      <MenuItem
        key={x.id}
        value={x.id}
        data-testid={`${testId}-menu-item-${x.id}`}
      >
        {translateBackend(x.translatedName)}
      </MenuItem>
    ));
  };

  return (
    <>
      {permissions.includes('read:asset_classes') ? (
        <SelectField
          testId={`${testId}-select-field-${label}`}
          leadingIcon={leadingIcon}
          fullWidth
          value={data ? value : ''}
          label={label}
          sx={{ width: '100%' }}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setAssetClass(event);
            if (onSelectFull) {
              const selected = filteredList.find(
                (x) => x.id === event.target.value,
              );
              if (selected) onSelectFull(selected);
            }
          }}
          size={size}
          disabled={!isNull(disabledWith)}
          required={required}
        >
          {!omitAny && (
            <MenuItem data-testid={`${testId}-menu-item-any`} value='ANY'>
              {t('components:any')}
            </MenuItem>
          )}
          {renderMenuItems()}
        </SelectField>
      ) : (
        <></>
      )}
    </>
  );
};

export default AssetClassSelect;
